/**
 * Generic admin CRUD table with dialog-based edit/create.
 * fields: [{ key, label, type, options?, required?, colSpan?, placeholder? }]
 * type: text, number, textarea, select, boolean, json, image, tags, password
 */
import React from "react";
import { Plus, Edit2, Trash2, X } from "lucide-react";
import api, { MEDIA } from "../../lib/api";
import { toast } from "sonner";

export default function AdminCrud({ title, endpoint, idField, columns, fields, defaultItem = {}, testPrefix = "crud" }) {
    const [items, setItems] = React.useState([]);
    const [open, setOpen] = React.useState(false);
    const [editing, setEditing] = React.useState(null);
    const [loading, setLoading] = React.useState(false);

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(endpoint);
            setItems(res.data || []);
        } finally {
            setLoading(false);
        }
    }, [endpoint]);

    React.useEffect(() => { load(); }, [load]);

    const save = async (doc) => {
        try {
            if (editing && editing[idField]) {
                await api.put(`${endpoint}/${editing[idField]}`, doc);
                toast.success("Actualizat");
            } else {
                await api.post(endpoint, doc);
                toast.success("Creat");
            }
            setOpen(false); setEditing(null);
            load();
        } catch (e) {
            toast.error("Eroare: " + (e.response?.data?.detail || e.message));
        }
    };

    const del = async (id) => {
        if (!window.confirm("Sigur ștergi?")) return;
        try {
            await api.delete(`${endpoint}/${id}`);
            toast.success("Șters");
            load();
        } catch (e) {
            toast.error("Eroare la ștergere");
        }
    };

    const openNew = () => { setEditing({ ...defaultItem }); setOpen(true); };
    const openEdit = (it) => { setEditing({ ...it }); setOpen(true); };

    return (
        <div data-testid={`admin-${testPrefix}`}>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <div className="overline mb-1">Administrare</div>
                    <h1 className="font-heading text-3xl uppercase">{title}</h1>
                </div>
                <button onClick={openNew} className="btn-primary flex items-center gap-2" data-testid={`${testPrefix}-new`}>
                    <Plus className="h-4 w-4" /> Adaugă
                </button>
            </div>
            <div className="border-2 border-foreground overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-foreground text-background">
                        <tr>
                            {columns.map(c => <th key={c.key} className="text-left px-3 py-2 font-mono text-xs uppercase">{c.label}</th>)}
                            <th className="px-3 py-2 w-32"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={columns.length + 1} className="text-center py-8 font-mono text-muted-foreground">Se încarcă...</td></tr>}
                        {!loading && items.length === 0 && <tr><td colSpan={columns.length + 1} className="text-center py-8 font-mono text-muted-foreground">Nu există înregistrări</td></tr>}
                        {items.map((it, idx) => (
                            <tr key={it[idField] || idx} className="border-t border-border hover:bg-muted" data-testid={`${testPrefix}-row-${idx}`}>
                                {columns.map(c => (
                                    <td key={c.key} className="px-3 py-2 align-top">
                                        {c.render ? c.render(it) : c.type === "image" ? (it[c.key] ? <img src={MEDIA(it[c.key])} alt="" className="w-12 h-12 object-cover" /> : "—") : c.type === "boolean" ? (it[c.key] ? "✓" : "—") : String(it[c.key] ?? "").slice(0, 80)}
                                    </td>
                                ))}
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => openEdit(it)} className="btn-ghost" data-testid={`${testPrefix}-edit-${idx}`}><Edit2 className="h-4 w-4" /></button>
                                    <button onClick={() => del(it[idField])} className="btn-ghost text-destructive" data-testid={`${testPrefix}-delete-${idx}`}><Trash2 className="h-4 w-4" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {open && editing && (
                <FormDialog fields={fields} value={editing} onChange={setEditing} onClose={() => { setOpen(false); setEditing(null); }} onSave={save} testPrefix={testPrefix} />
            )}
        </div>
    );
}

function FormDialog({ fields, value, onChange, onClose, onSave, testPrefix }) {
    const submit = async (e) => {
        e.preventDefault();
        onSave(value);
    };
    const set = (k, v) => onChange({ ...value, [k]: v });

    const uploadImage = async (file, k) => {
        const fd = new FormData();
        fd.append("file", file);
        try {
            const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            set(k, res.data.url);
            toast.success("Upload reușit");
        } catch {
            toast.error("Eroare upload");
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
            <form onSubmit={submit} className="bg-card border-2 border-foreground max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={`${testPrefix}-dialog`}>
                <div className="flex items-center justify-between p-4 border-b-2 border-foreground">
                    <h2 className="font-heading text-xl uppercase">{value?._new ? "Creează" : "Editează"}</h2>
                    <button type="button" onClick={onClose}><X className="h-5 w-5" /></button>
                </div>
                <div className="p-5 grid grid-cols-2 gap-3">
                    {fields.map(f => (
                        <div key={f.key} className={f.colSpan === 2 ? "col-span-2" : "col-span-2 md:col-span-1"}>
                            <label className="block font-mono text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{f.label}{f.required && " *"}</label>
                            {f.type === "textarea" && <textarea required={f.required} value={value[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} rows={4} className="w-full px-3 py-2 border border-foreground bg-transparent text-sm" />}
                            {f.type === "select" && (
                                <select value={value[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="w-full px-3 py-2 border border-foreground bg-transparent text-sm">
                                    <option value="">—</option>
                                    {(f.options || []).map(o => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
                                </select>
                            )}
                            {f.type === "boolean" && <label className="flex items-center gap-2"><input type="checkbox" checked={!!value[f.key]} onChange={(e) => set(f.key, e.target.checked)} /><span className="text-sm">Activ</span></label>}
                            {f.type === "number" && <input type="number" step="0.01" value={value[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value === "" ? null : Number(e.target.value))} className="w-full px-3 py-2 border border-foreground bg-transparent text-sm" />}
                            {f.type === "image" && (
                                <div className="flex gap-2 items-center">
                                    <input value={value[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} placeholder="URL imagine sau upload..." className="flex-1 px-3 py-2 border border-foreground bg-transparent text-sm" />
                                    <label className="btn-outline cursor-pointer text-xs">
                                        Upload
                                        <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0], f.key)} />
                                    </label>
                                </div>
                            )}
                            {f.type === "json" && <textarea value={typeof value[f.key] === "object" ? JSON.stringify(value[f.key], null, 2) : (value[f.key] || "{}")} onChange={(e) => { try { set(f.key, JSON.parse(e.target.value)); } catch { set(f.key, e.target.value); } }} rows={4} className="w-full px-3 py-2 border border-foreground bg-transparent text-sm font-mono text-xs" />}
                            {f.type === "tags" && <input value={(value[f.key] || []).join(", ")} onChange={(e) => set(f.key, e.target.value.split(",").map(s => s.trim()).filter(Boolean))} className="w-full px-3 py-2 border border-foreground bg-transparent text-sm" placeholder="separate prin virgulă" />}
                            {(!f.type || f.type === "text" || f.type === "password") && <input type={f.type === "password" ? "password" : "text"} required={f.required} value={value[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder || ""} className="w-full px-3 py-2 border border-foreground bg-transparent text-sm" />}
                        </div>
                    ))}
                </div>
                <div className="p-4 border-t-2 border-foreground flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="btn-outline">Anulează</button>
                    <button type="submit" className="btn-primary" data-testid={`${testPrefix}-save`}>Salvează</button>
                </div>
            </form>
        </div>
    );
}
